@if (loading) {
  <div class="flex items-center justify-center min-h-100">
    <div class="loading loading-spinner loading-lg"></div>
  </div>
} @else if (accessDenied) {
  <div class="alert alert-error">
    You don't have access to this household.
  </div>
} @else if (household) {
  <!-- GitHub-style settings layout -->
  <div class="flex flex-col lg:flex-row gap-6 lg:gap-8 w-full p-3 sm:p-4 lg:p-6">
    <!-- Sidebar Menu (left) -->
    <aside class="w-full lg:w-64 flex-none">
      <nav
        class="flex flex-row lg:flex-col overflow-x-auto lg:overflow-x-visible gap-1 lg:gap-0 sticky top-4 bg-base-100 lg:bg-transparent p-2 lg:p-0 rounded-lg lg:rounded-none border lg:border-0 border-base-300 hide-scrollbar">
        @for (item of menuItems; track item.id) {
          <button
            (click)="selectSection(item.id)"
            [class]="'px-4 py-2.5 text-left text-sm font-medium rounded-lg transition-all duration-200 whitespace-nowrap border cursor-pointer ' +
                     (isActiveSection(item.id)
                       ? 'text-base-content bg-base-200 border-base-300 font-semibold'
                       : 'text-base-content/70 hover:text-base-content hover:bg-base-200 border-transparent')"
          >
            {{ item.label }}
          </button>
        }
      </nav>
    </aside>

    <!-- Content Area (right) -->
    <main class="flex-1 min-w-0">

      <!-- GENERAL SECTION -->
      @if (activeSection === 'general') {
        <div class="space-y-6">
          <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 pb-4 border-b border-base-300">
            <div>
              <h2 class="text-xl sm:text-2xl font-bold">General</h2>
              <p class="text-sm opacity-70 mt-1">Manage your household settings</p>
            </div>
            <ui-button [variant]="'primary'" [outline]="true" size="sm" (clicked)="openEditHousehold()">
              <fa-icon [icon]="faEdit"></fa-icon>
              <span>Edit</span>
            </ui-button>
          </div>

          <ui-card [shadow]="false">
            <div class="space-y-4">
              <div
                class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 sm:gap-3 py-2 border-b border-base-200">
                <span class="font-medium text-sm opacity-70 min-w-35">Household Name</span>
                <span class="text-sm font-semibold flex items-center gap-2">{{ household.name }}</span>
              </div>
              <div
                class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 sm:gap-3 py-2 border-b border-base-200">
                <span class="font-medium text-sm opacity-70 min-w-35">Default Household</span>
                <span class="text-sm font-semibold flex items-center gap-2">
                  @if (household.is_default) {
                    <ui-badge [variant]="'success'">Yes</ui-badge>
                  } @else {
                    <ui-badge [variant]="'neutral'">No</ui-badge>
                  }
                </span>
              </div>
              <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 sm:gap-3 py-2">
                <span class="font-medium text-sm opacity-70 min-w-35">You are</span>
                <span class="text-sm font-semibold flex items-center gap-2">
                  @if (household.is_owner) {
                    <ui-badge [variant]="'primary'">Owner</ui-badge>
                  } @else {
                    <ui-badge [variant]="'neutral'">Member</ui-badge>
                  }
                </span>
              </div>
            </div>
          </ui-card>
        </div>
      }

      <!-- MEMBERS SECTION -->
      @if (activeSection === 'members') {
        <div class="space-y-6">
          <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 pb-4 border-b border-base-300">
            <div>
              <h2 class="text-xl sm:text-2xl font-bold">Members & Invitations</h2>
              <p class="text-sm opacity-70 mt-1">Manage members and send invitations</p>
            </div>
            <ui-button [variant]="'primary'" [outline]="true" size="sm" (clicked)="openInviteModal()">
              Invite Member
            </ui-button>
          </div>

          <!-- Members List -->
          <ui-card [shadow]="false">
            <h3 class="text-lg font-semibold mb-4">Members</h3>
            <ui-list [variant]="'bordered'" [boxed]="true" [compact]="true">
              @for (m of household.members; track m) {
                <ui-list-item>
                  <div class="flex items-center justify-between gap-3 w-full">
                    <div class="min-w-0">
                      <div class="flex flex-wrap items-center gap-2">
                        <span class="font-medium truncate">{{ m.nickname }}</span>
                        @if (m.is_owner) {
                          <ui-badge [variant]="'neutral'">Owner</ui-badge>
                        }
                      </div>
                      <div class="text-sm opacity-70 truncate">{{ m.email }}</div>
                    </div>
                    @if (household.is_owner && !m.is_owner) {
                      <ui-button [variant]="'danger'" [outline]="true" size="sm" (clicked)="confirmRemoveMember(m)">
                        <fa-icon [icon]="faRemove"></fa-icon>
                      </ui-button>
                    }
                  </div>
                </ui-list-item>
              }
            </ui-list>
          </ui-card>

          <!-- Invitations -->
          @if (openInvites && openInvites.length > 0) {
            <ui-card className="mt-5" [shadow]="false">
              <h3 class="text-lg font-semibold mb-4">Pending Invitations</h3>
              <ui-list [variant]="'bordered'" [boxed]="true" [compact]="true">
                @for (i of openInvites; track i) {
                  <ui-list-item>
                    <div class="flex items-center justify-between gap-3 w-full">
                      <div class="min-w-0 flex items-center gap-3">
                        <ui-badge
                          [variant]="i.state === 'ACCEPTED' ? 'success' : (i.state === 'REJECTED' ? 'error' : 'neutral')"
                        >
                          {{ i.state | titlecase }}
                        </ui-badge>

                        <span class="text-xs opacity-60 whitespace-nowrap">{{ i.invited_at | date:'d. MMM yy' }}</span>

                        <span class="font-semibold truncate"
                              [ngClass]="{'line-through text-error': i.state == 'REJECTED'}">
                          {{ i.invitee_email }}
                        </span>
                      </div>

                      <div class="flex items-center gap-2 shrink-0">
                        @if (i.state == 'PENDING') {
                          <ui-button [variant]="'danger'" [outline]="true" size="sm" (clicked)="revokeInvitation(i.id)">
                            <fa-icon [icon]="faRemove"></fa-icon>
                            Revoke
                          </ui-button>
                        }
                        @if (i.state == 'REJECTED') {
                          <ui-button [variant]="'primary'" [outline]="true" size="sm"
                                     (clicked)="resendInvitation(i.id)">
                            <fa-icon [icon]="faResend"></fa-icon>
                            Resend
                          </ui-button>
                        }
                      </div>
                    </div>
                  </ui-list-item>
                }
              </ui-list>
            </ui-card>
          }
        </div>
      }

      <!-- STORES SECTION -->
      @if (activeSection === 'stores') {
        <div class="space-y-6">
          <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 pb-4 border-b border-base-300">
            <div>
              <h2 class="text-xl sm:text-2xl font-bold">Stores</h2>
              <p class="text-sm opacity-70 mt-1">Manage your household stores</p>
            </div>
            <ui-button [variant]="'primary'" [outline]="true" size="sm" (clicked)="openCreateStoreModal()">
              Add Store
            </ui-button>
          </div>

          <ui-card [shadow]="false">
            <ui-list [variant]="'default'" [boxed]="false" [compact]="true">
              @for (s of household.stores; track s) {
                <ui-list-item>
                  <div class="flex items-center justify-between gap-3 w-full">
                    <div class="min-w-0">
                      <div class="flex flex-wrap items-center gap-2">
                        <span class="font-semibold truncate">{{ s.name }}</span>
                        @if (s.is_default) {
                          <ui-badge [variant]="'primary'">Default</ui-badge>
                        }
                      </div>
                    </div>

                    <ui-row-actions [actions]="storeRowActions(s)" [mode]="'icon'"></ui-row-actions>
                  </div>
                </ui-list-item>
              }

              @if (!household.stores || household.stores.length === 0) {
                <div class="p-4 text-sm opacity-60">No stores yet.</div>
              }
            </ui-list>
          </ui-card>
        </div>
      }

      <!-- TASKS SECTION -->
      @if (activeSection === 'tasks') {
        <div class="space-y-6">
          <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 pb-4 border-b border-base-300">
            <div>
              <h2 class="text-xl sm:text-2xl font-bold">Household Tasks</h2>
              <p class="text-sm opacity-70 mt-1">Execute maintenance tasks for your household</p>
            </div>
          </div>

          <ui-card [shadow]="false">
            <div class="space-y-4">
              <p class="text-sm opacity-70">Available tasks help maintain your household data, such as categorizing
                products, processing quantities, syncing, and generating embeddings.</p>

              <div class="divider my-2"></div>

              <h3 class="text-lg font-semibold mb-3">Select Tasks to Execute</h3>

              <div class="space-y-3">
                <label
                  class="flex items-start gap-3 p-3 rounded-lg border border-base-300 hover:bg-base-200 cursor-pointer transition-colors">
                  <input
                    type="checkbox"
                    class="checkbox checkbox-primary mt-0.5"
                    [checked]="selectedTasks.includes('categorize_product')"
                    (change)="onTaskToggle('categorize_product', $event.target['checked'])"
                    [disabled]="tasksExecuting"
                  />
                  <div class="flex-1">
                    <span class="font-medium block">Categorize Products</span>
                    <span class="text-sm opacity-60">Automatically categorize products based on their attributes</span>
                  </div>
                </label>

                <label
                  class="flex items-start gap-3 p-3 rounded-lg border border-base-300 hover:bg-base-200 cursor-pointer transition-colors">
                  <input
                    type="checkbox"
                    class="checkbox checkbox-primary mt-0.5"
                    [checked]="selectedTasks.includes('process_product_quantity')"
                    (change)="onTaskToggle('process_product_quantity', $event.target['checked'])"
                    [disabled]="tasksExecuting"
                  />
                  <div class="flex-1">
                    <span class="font-medium block">Process Product Quantity</span>
                    <span class="text-sm opacity-60">Process and normalize product quantities</span>
                  </div>
                </label>

                <label
                  class="flex items-start gap-3 p-3 rounded-lg border border-base-300 hover:bg-base-200 cursor-pointer transition-colors">
                  <input
                    type="checkbox"
                    class="checkbox checkbox-primary mt-0.5"
                    [checked]="selectedTasks.includes('force_product_sync')"
                    (change)="onTaskToggle('force_product_sync', $event.target['checked'])"
                    [disabled]="tasksExecuting"
                  />
                  <div class="flex-1">
                    <span class="font-medium block">Force Product Sync</span>
                    <span class="text-sm opacity-60">Force synchronization of all products</span>
                  </div>
                </label>

                <label
                  class="flex items-start gap-3 p-3 rounded-lg border border-base-300 hover:bg-base-200 cursor-pointer transition-colors">
                  <input
                    type="checkbox"
                    class="checkbox checkbox-primary mt-0.5"
                    [checked]="selectedTasks.includes('generate_product_embeddings')"
                    (change)="onTaskToggle('generate_product_embeddings', $event.target['checked'])"
                    [disabled]="tasksExecuting"
                  />
                  <div class="flex-1">
                    <span class="font-medium block">Generate Product Embeddings</span>
                    <span class="text-sm opacity-60">Generate AI embeddings for better product search</span>
                  </div>
                </label>

                <label
                  class="flex items-start gap-3 p-3 rounded-lg border border-base-300 hover:bg-base-200 cursor-pointer transition-colors">
                  <input
                    type="checkbox"
                    class="checkbox checkbox-primary mt-0.5"
                    [checked]="selectedTasks.includes('assign_product_kind')"
                    (change)="onTaskToggle('assign_product_kind', $event.target['checked'])"
                    [disabled]="tasksExecuting"
                  />
                  <div class="flex-1">
                    <span class="font-medium block">Assign Product Kind</span>
                    <span
                      class="text-sm opacity-60">Match products against the catalog and assign their product kind</span>
                  </div>
                </label>

                <label
                  class="flex items-start gap-3 p-3 rounded-lg border border-base-300 hover:bg-base-200 cursor-pointer transition-colors">
                  <input
                    type="checkbox"
                    class="checkbox checkbox-primary mt-0.5"
                    [checked]="selectedTasks.includes('classify_recipes')"
                    (change)="onTaskToggle('classify_recipes', $event.target['checked'])"
                    [disabled]="tasksExecuting"
                  />
                  <div class="flex-1">
                    <span class="font-medium block">Reclassify Recipes</span>
                    <span class="text-sm opacity-60">Re-run automatic course, season and dish-type classification for every recipe in this household. Recipes with manually edited classification are left untouched.</span>
                  </div>
                </label>

                <label
                  class="flex items-start gap-3 p-3 rounded-lg border border-base-300 hover:bg-base-200 cursor-pointer transition-colors">
                  <input
                    type="checkbox"
                    class="checkbox checkbox-primary mt-0.5"
                    [checked]="selectedTasks.includes('backfill_stock_prices')"
                    (change)="onTaskToggle('backfill_stock_prices', $event.target['checked'])"
                    [disabled]="tasksExecuting"
                  />
                  <div class="flex-1">
                    <span class="font-medium block">Backfill Stock Prices</span>
                    <span class="text-sm opacity-60">Fill in missing prices for stock and past consumption from the catalog price history, using each product's first enabled provider. Prices entered manually or read from a receipt are never overwritten.</span>
                  </div>
                </label>
              </div>

              @if (selectedTasks.length > 0) {
                <div class="alert alert-info mt-4">
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"
                       class="stroke-current shrink-0 w-6 h-6">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                          d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                  </svg>
                  <span class="text-sm">{{ selectedTasks.length }} task{{ selectedTasks.length > 1 ? 's' : '' }}
                    selected</span>
                </div>
              }

              <div class="flex justify-end pt-4">
                <ui-button
                  [variant]="'primary'"
                  (clicked)="executeSelectedTasks()"
                  [disabled]="tasksExecuting || selectedTasks.length === 0"
                  [loading]="tasksExecuting"
                >
                  @if (tasksExecuting) {
                    <span>Executing...</span>
                  } @else {
                    <span>Execute {{ selectedTasks.length > 0 ? selectedTasks.length : '' }}
                      Task{{ selectedTasks.length !== 1 ? 's' : '' }}</span>
                  }
                </ui-button>
              </div>
            </div>
          </ui-card>
        </div>
      }

      <!-- EXPORT STOCK SECTION -->
      @if (activeSection === 'export') {
        <div class="space-y-6">
          <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 pb-4 border-b border-base-300">
            <div>
              <h2 class="text-xl sm:text-2xl font-bold">Export Stock</h2>
              <p class="text-sm opacity-70 mt-1">Export your pantry stock for use with AI assistants</p>
            </div>
          </div>

          <ui-card [shadow]="false">
            <div class="space-y-4">
              <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 py-2">
                <div>
                  <p class="font-medium">Export as LLM Prompt</p>
                  <p class="text-sm opacity-60 mt-0.5">Generate a structured snapshot of your pantry for use with AI assistants like ChatGPT or Claude.</p>
                </div>
                <ui-button [variant]="'primary'" [outline]="true" size="sm" (clicked)="openExportPromptModal()">
                  <fa-icon [icon]="faDownload"></fa-icon>
                  <span>Export</span>
                </ui-button>
              </div>
            </div>
          </ui-card>
        </div>
      }

      <!-- PROVIDERS SECTION -->
      @if (activeSection === 'providers') {
        <div class="space-y-6">
          <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 pb-4 border-b border-base-300">
            <div>
              <h2 class="text-xl sm:text-2xl font-bold">Catalog Providers</h2>
              <p class="text-sm opacity-70 mt-1">Enable or disable providers and drag to set priority — providers higher in the list are preferred when looking up products.</p>
            </div>
          </div>

          <ui-card [shadow]="false">
            @if (loadingProviders) {
              <div class="text-sm opacity-50 py-4">Loading providers…</div>
            } @else if (providers.length === 0) {
              <div class="text-sm opacity-50 py-4">No catalog providers configured.</div>
            } @else {
              <ul
                cdkDropList
                (cdkDropListDropped)="dropProvider($event)"
                class="space-y-2"
              >
                @for (provider of providers; track provider.provider_key) {
                  <li
                    cdkDrag
                    class="flex items-center gap-3 rounded-md border border-base-300 bg-base-100 px-3 py-2.5"
                  >
                    <span class="text-base-content/40 shrink-0 cursor-grab" cdkDragHandle>
                      <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 8h16M4 16h16"/>
                      </svg>
                    </span>
                    <img
                      [src]="getProviderLogoPath(provider.provider_key)"
                      [alt]="provider.provider_name || provider.provider_key"
                      class="h-5 w-5 rounded object-contain shrink-0"
                    />
                    <span class="flex-1 text-sm font-medium">{{ provider.provider_name || provider.provider_key }}</span>
                    <input
                      type="checkbox"
                      class="toggle toggle-sm toggle-primary"
                      [checked]="provider.is_enabled"
                      (change)="toggleProvider(provider)"
                    />
                  </li>
                }
              </ul>
            }
          </ui-card>
        </div>
      }

      <!-- FOOD PREFERENCES SECTION -->
      @if (activeSection === 'preferences') {
        <div class="space-y-6">
          <div class="pb-4 border-b border-base-300">
            <h2 class="text-xl sm:text-2xl font-bold">Food Preferences</h2>
            <p class="text-sm opacity-70 mt-1">
              Used by the assistant when it suggests or creates recipes. Diets and disliked ingredients are
              always respected; liked cuisines and custom notes are gentle steers. Separate multiple entries
              with commas.
            </p>
          </div>

          <ui-card [shadow]="false">
            <div class="space-y-4">
              <label class="block">
                <span class="text-sm font-medium">Diets</span>
                <input class="input input-bordered w-full mt-1" [(ngModel)]="dietsText"
                       [disabled]="loadingPrefs" placeholder="e.g. vegetarian, vegan" />
              </label>
              <label class="block">
                <span class="text-sm font-medium">Disliked ingredients</span>
                <input class="input input-bordered w-full mt-1" [(ngModel)]="dislikesText"
                       [disabled]="loadingPrefs" placeholder="e.g. cheese, coriander" />
              </label>
              <label class="block">
                <span class="text-sm font-medium">Liked cuisines</span>
                <input class="input input-bordered w-full mt-1" [(ngModel)]="cuisinesText"
                       [disabled]="loadingPrefs" placeholder="e.g. mediterranean, thai" />
              </label>
              <label class="block">
                <span class="text-sm font-medium">Default portion size</span>
                <input type="number" min="0" max="50" step="1"
                       class="input input-bordered w-full mt-1 sm:max-w-[12rem]"
                       [(ngModel)]="defaultServings" [disabled]="loadingPrefs"
                       placeholder="e.g. 2" />
                <span class="text-xs opacity-60 mt-1 block">
                  Number of people the assistant sizes recipes and meal plans for. Leave at 0 to let it
                  choose per dish.
                </span>
              </label>
              <label class="block">
                <span class="text-sm font-medium">Custom instructions</span>
                <textarea class="textarea textarea-bordered w-full mt-1" rows="3" [(ngModel)]="customPrompt"
                          [disabled]="loadingPrefs"
                          placeholder="e.g. I like quick weeknight meals and don't eat pork"></textarea>
              </label>
              <ui-button [variant]="'primary'" size="sm" (clicked)="saveFoodPreferences()"
                         [disabled]="savingPrefs || loadingPrefs" [loading]="savingPrefs">
                Save preferences
              </ui-button>
            </div>
          </ui-card>
        </div>
      }

      <!-- REMINDERS SECTION -->
      @if (activeSection === 'reminders') {
        <div class="space-y-6">
          <div class="pb-4 border-b border-base-300">
            <h2 class="text-xl sm:text-2xl font-bold">Reminders</h2>
            <p class="text-sm opacity-70 mt-1">
              Every few days we ask whether one or two things you have not touched in a while are
              already used up. Answering keeps your stock honest; ignoring a question changes
              nothing. These settings apply to everyone in the household.
            </p>
          </div>

          @if (remindersUnavailable) {
            <div class="alert alert-warning">
              <span class="text-sm">
                This server does not have reminders yet — the values below are the defaults and
                cannot be saved.
              </span>
            </div>
          }

          <ui-card [shadow]="false">
            <div class="space-y-5">

              <label class="flex items-start gap-3">
                <input
                  type="checkbox"
                  class="toggle toggle-primary mt-0.5"
                  [(ngModel)]="reminders.enabled"
                  [disabled]="loadingReminders"/>
                <span>
                  <span class="text-sm font-medium block">Ask about forgotten items</span>
                  <span class="text-xs opacity-60 block mt-0.5">
                    Off means nobody in this household is asked, on any device.
                  </span>
                </span>
              </label>

              <label class="block">
                <span class="text-sm font-medium">How often</span>
                <select
                  class="select select-bordered w-full mt-1 sm:max-w-[16rem]"
                  [(ngModel)]="reminders.cadenceDays"
                  [disabled]="loadingReminders || !reminders.enabled">
                  @for (days of cadenceChoices; track days) {
                    <option [ngValue]="days">
                      @if (days === 1) { Every day } @else { Every {{ days }} days }
                    </option>
                  }
                </select>
                <span class="text-xs opacity-60 mt-1 block">
                  At most one round per period — and none at all when nothing has been sitting long
                  enough to wonder about.
                </span>
              </label>

              <label class="block">
                <span class="text-sm font-medium">Questions per round</span>
                <select
                  class="select select-bordered w-full mt-1 sm:max-w-[16rem]"
                  [(ngModel)]="reminders.itemsPerRound"
                  [disabled]="loadingReminders || !reminders.enabled">
                  @for (n of itemsPerRoundChoices; track n) {
                    <option [ngValue]="n">{{ n }}</option>
                  }
                </select>
                <span class="text-xs opacity-60 mt-1 block">
                  Two is enough to be worth a look and short enough to finish.
                </span>
              </label>

              <div class="block">
                <span class="text-sm font-medium">Only ask between</span>
                <div class="flex items-center gap-2 mt-1">
                  <select
                    class="select select-bordered w-full sm:max-w-[8rem]"
                    aria-label="Quiet hours start"
                    [(ngModel)]="reminders.quietHoursStart"
                    [disabled]="loadingReminders || !reminders.enabled">
                    @for (h of hourChoices; track h) {
                      <option [ngValue]="h">{{ hourLabel(h) }}</option>
                    }
                  </select>
                  <span class="text-sm opacity-70">and</span>
                  <select
                    class="select select-bordered w-full sm:max-w-[8rem]"
                    aria-label="Quiet hours end"
                    [(ngModel)]="reminders.quietHoursEnd"
                    [disabled]="loadingReminders || !reminders.enabled">
                    @for (h of hourChoices; track h) {
                      <option [ngValue]="h">{{ hourLabel(h) }}</option>
                    }
                  </select>
                </div>
                <span class="text-xs opacity-60 mt-1 block">
                  Nothing is sent outside these hours. Times are in {{ reminders.timezone }}.
                </span>
              </div>

              <ui-button
                [variant]="'primary'"
                size="sm"
                (clicked)="saveReminderSettings()"
                [disabled]="savingReminders || loadingReminders || remindersUnavailable"
                [loading]="savingReminders">
                Save reminder settings
              </ui-button>
            </div>
          </ui-card>

          <ui-card [shadow]="false">
            <div class="space-y-2">
              <h3 class="text-sm font-semibold">Products we never ask about</h3>
              <p class="text-xs opacity-60">
                Mute a product from the ⋯ menu on its question. There is no list of muted products
                here yet — the server has no endpoint to read one back.
              </p>
            </div>
          </ui-card>
        </div>
      }

      <!-- DANGER ZONE SECTION -->
      @if (activeSection === 'danger') {
        <div class="space-y-6">
          <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 pb-4 border-b border-base-300">
            <div>
              <h2 class="text-xl sm:text-2xl font-bold text-error">Danger Zone</h2>
              <p class="text-sm opacity-70 mt-1">Irreversible and destructive actions</p>
            </div>
          </div>

          <div class="border-2 border-error/30 rounded-lg p-4 bg-error/5">
            <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div>
                <h3 class="font-semibold text-error mb-1">Delete this household</h3>
                @if (household.is_default) {
                  <p class="text-sm opacity-70">This is your default household. To delete it, switch your default
                    household first.</p>
                } @else {
                  <p class="text-sm opacity-70">Deleting this household will also delete all your products and cannot be
                    undone.</p>
                }
              </div>
              @if (!household.is_default) {
                <ui-button
                  [variant]="'danger'"
                  size="sm"
                  (clicked)="openDeleteHouseholdModal()"
                  [disabled]="deletingHousehold"
                  [loading]="deletingHousehold"
                >
                  Delete household
                </ui-button>
              }
            </div>
          </div>

          <div class="border-2 border-error/30 rounded-lg p-4 bg-error/5">
            <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div>
                <h3 class="font-semibold text-error mb-1">Clear products</h3>
                <p class="text-sm opacity-70">Removes all product data for this household, including stock levels,
                  desired stock settings, and shopping list item references. Products themselves are not deleted.</p>
              </div>
              <ui-button
                [variant]="'danger'"
                size="sm"
                (clicked)="openClearProductsModal()"
                [disabled]="clearingProducts"
                [loading]="clearingProducts"
              >
                Clear products
              </ui-button>
            </div>
          </div>
        </div>
      }

    </main>
  </div>

  <!-- MODALS -->

    <!-- Edit Household Modal -->
  <ui-modal #editHouseholdUiModal title="Edit Household" size="md">
    <form [formGroup]="householdForm" class="space-y-4" (ngSubmit)="saveHouseholdFromModal()">
      <label class="label cursor-pointer justify-start gap-3">
        <input type="checkbox" class="toggle" formControlName="isDefault"/>
        <span class="label-text">Default household</span>
      </label>

      <div class="form-control">
        <label class="label" for="household-name">
          <span class="label-text">Name</span>
        </label>
        <input id="household-name" type="text" class="input input-bordered w-full" formControlName="name"
               placeholder="Household name"/>
        @if (householdForm.get('name')?.invalid && (householdForm.get('name')?.dirty || householdForm.get('name')?.touched)) {
          <label class="label">
            <span class="label-text-alt text-error">Please enter a name (min 2 chars).</span>
          </label>
        }
      </div>
    </form>

    <div modal-footer class="w-full flex justify-end gap-2">
      <ui-button [variant]="'secondary'" [outline]="true" (clicked)="closeEditHouseholdModal()"
                 [disabled]="householdSaving">Cancel
      </ui-button>
      <ui-button [variant]="'primary'" (clicked)="saveHouseholdFromModal()"
                 [disabled]="householdSaving || householdForm.invalid" [loading]="householdSaving">
        Save
      </ui-button>
    </div>
  </ui-modal>

  <!-- Store Modal -->
  <ui-modal #storeUiModal [title]="storeMode === 'create' ? 'Add Store' : 'Edit Store'" size="md">
    @if (storeMessage) {
      <div class="alert alert-info mb-3">
        {{ storeMessage }}
      </div>
    }

    <form [formGroup]="storeForm" class="space-y-4" (ngSubmit)="saveStoreFromModal()">
      <label class="label cursor-pointer justify-start gap-3">
        <input type="checkbox" class="toggle" formControlName="isDefault"/>
        <span class="label-text">Default store</span>
      </label>

      <div class="form-control">
        <label class="label" for="store-name">
          <span class="label-text">Name</span>
        </label>
        <input id="store-name" type="text" class="input input-bordered w-full" formControlName="name"
               placeholder="Eg. Kitchen"/>
        @if (storeForm.get('name')?.invalid && (storeForm.get('name')?.dirty || storeForm.get('name')?.touched)) {
          <label class="label">
            <span class="label-text-alt text-error">Please enter a name (min 2 chars).</span>
          </label>
        }
      </div>

      <div class="form-control">
        <label class="label" for="store-temperature-zone">
          <span class="label-text">Default temperature zone</span>
        </label>
        <select id="store-temperature-zone" class="select select-bordered w-full"
                formControlName="temperatureZone">
          <option value="">No default</option>
          @for (zone of temperatureZones; track zone.value) {
            <option [value]="zone.value">{{ zone.label }}</option>
          }
        </select>
        <p class="mt-1.5 text-xs opacity-70 leading-snug whitespace-normal">
          Items placed here default to this zone. Room temperature is used when unset.
        </p>
      </div>
    </form>

    <div modal-footer class="w-full flex justify-end gap-2">
      <ui-button [variant]="'secondary'" [outline]="true" (clicked)="closeStoreModal()" [disabled]="storeSaving">
        Cancel
      </ui-button>
      <ui-button [variant]="'primary'" (clicked)="saveStoreFromModal()" [disabled]="storeSaving || storeForm.invalid"
                 [loading]="storeSaving">
        Save
      </ui-button>
    </div>
  </ui-modal>

  <!-- Export Stock Prompt Modal -->
  <ui-modal #exportPromptModal title="LLM Prompt — Pantry Snapshot" size="lg">
    @if (stockPromptLoading) {
      <div class="flex items-center justify-center py-12">
        <div class="loading loading-spinner loading-lg"></div>
      </div>
    } @else if (stockPrompt) {
      <div class="space-y-3">
        <p class="text-sm opacity-60">Copy this prompt and paste it into your AI assistant to get recipe suggestions, meal plans, or shopping advice based on your pantry.</p>
        <div class="relative">
          <pre class="bg-base-200 rounded-lg p-4 text-sm font-mono whitespace-pre-wrap break-words overflow-auto max-h-96">{{ stockPrompt }}</pre>
          <button
            class="btn btn-sm btn-ghost absolute top-2 right-2 gap-1.5"
            (click)="copyStockPrompt()"
            [title]="stockPromptCopied ? 'Copied!' : 'Copy to clipboard'"
          >
            <fa-icon [icon]="stockPromptCopied ? faCheck : faCopy" [class]="stockPromptCopied ? 'text-success' : ''"></fa-icon>
            <span class="text-xs">{{ stockPromptCopied ? 'Copied!' : 'Copy' }}</span>
          </button>
        </div>
      </div>
    }

    <div modal-footer class="w-full flex justify-end">
      <ui-button [variant]="'secondary'" [outline]="true" (clicked)="exportPromptModal?.close()">Close</ui-button>
    </div>
  </ui-modal>

  <!-- Invite Member Modal -->
  <ui-modal #inviteModal title="Invite Member" size="md">
    <div class="space-y-4">
      <p class="text-sm opacity-70">Enter the email address of the person you want to invite to this household.</p>

      <div class="form-control">
        <label class="label" for="invite-email">
          <span class="label-text">Email Address</span>
        </label>
        <input
          id="invite-email"
          type="email"
          class="input input-bordered w-full"
          [(ngModel)]="inviteEmail"
          placeholder="colleague@example.com"
          [disabled]="inviting"
        />
      </div>
    </div>

    <div modal-footer class="w-full flex justify-end gap-2">
      <ui-button [variant]="'secondary'" [outline]="true" (clicked)="closeInviteModal()" [disabled]="inviting">Cancel
      </ui-button>
      <ui-button [variant]="'success'" (clicked)="sendInvitation()" [disabled]="inviting || !inviteEmail"
                 [loading]="inviting">
        Send Invitation
      </ui-button>
    </div>
  </ui-modal>

}
